/**
 * WFA Queue Context
 * 
 * React context provider for Walk Forward Analysis queue state.
 * Completely separate from optimization queue context.
 */

import React, { createContext, useContext, type ReactNode } from 'react';
import { useWFAQueue } from '../hooks/useWFAQueue';
import type { WFAJob, WFAConfig } from '../services/wfa/WFAApiService';

interface WFAQueueContextValue {
    // State
    jobs: WFAJob[];
    activeJobs: WFAJob[];
    completedJobs: WFAJob[];
    failedJobs: WFAJob[];
    loading: boolean;
    error: string | null;
    connected: boolean;

    // Actions
    startWFA: (config: WFAConfig) => Promise<string | null>;
    removeJob: (jobId: string) => Promise<void>;
    getJobDetails: (jobId: string) => Promise<WFAJob | null | undefined>;
    fetchJobs: () => Promise<void>;
    connect: () => void;
    disconnect: () => void;
}

const WFAQueueContext = createContext<WFAQueueContextValue | undefined>(undefined);

export interface WFAQueueProviderProps {
    children: ReactNode;
    autoConnect?: boolean;
    pollInterval?: number;
}

export const WFAQueueProvider: React.FC<WFAQueueProviderProps> = ({
    children,
    autoConnect = true,
    pollInterval = 10000
}) => {
    const wfaQueue = useWFAQueue({ autoConnect, pollInterval });

    return (
        <WFAQueueContext.Provider value={wfaQueue}>
            {children}
        </WFAQueueContext.Provider>
    );
};

/**
 * Hook to access WFA queue context
 */
export const useWFAQueueContext = (): WFAQueueContextValue => {
    const context = useContext(WFAQueueContext);

    if (context === undefined) {
        throw new Error('useWFAQueueContext must be used within a WFAQueueProvider');
    }

    return context;
};

export default WFAQueueContext;
